import { AxiosResponse } from 'axios';
import ApiService from './apiService';
import { PaginatedResponse, PaginationParams } from './types';

export type CourseCompletionResponse = {
  courseId: number;
  userId: number;
  started: boolean;
  selfAssessmentCompletionPercentage: number;
  quizCompleted: boolean;
  projectCompleted: boolean;
  courseCompleted: boolean;
};

export const CourseService = {
  getOne: async (id: number): Promise<Course> => {
    const response = await ApiService.get(`/courses/${id}`);
    return response.data;
  },

  getAll: async (params?: PaginationParams & { searchText?: string }) => {
    const response = await ApiService.get<PaginatedResponse<Course>>(`/courses`, params);
    return response.data;
  },

  search: async (searchText: string, page = 1, excludeIds?: number[]): Promise<Lesson[]> => {
    const response = await ApiService.get('/courses', {
      searchText,
      page,
      excludeIds,
    });
    return response.data.data;
  },

  create: async (data: Course): Promise<Course> => {
    const response = await ApiService.post(`/courses`, data);
    return response.data;
  },

  update: async (id: number, data: Course): Promise<Course> => {
    const response = await ApiService.put(`/courses/${id}`, data);
    return response.data;
  },

  getCourseLessons: async (id: number): Promise<Lesson[]> => {
    const response = await ApiService.get(`/courses/${id}/lessons`);
    return response.data;
  },

  getStudents: async (id: number): Promise<User[]> => {
    const response = await ApiService.get(`/courses/${id}/students`);
    return response.data;
  },
  getInstructors: async (id: number): Promise<User[]> => {
    const response = await ApiService.get(`/courses/${id}/instructors`);
    return response.data;
  },

  addInstructor: async (id: number, data: { userId: number }): Promise<User> => {
    const response = await ApiService.post(`/courses/${id}/instructors`, data);
    return response.data;
  },

  deleteInstructor: async (id: number, userId: number): Promise<User> => {
    const response = await ApiService.delete(`/courses/${id}/instructors/${userId}`);
    return response.data;
  },

  addMainInstructor: async (
    id: number,
    data: {
      userId: number;
      addRemove: boolean;
    }
  ): Promise<User> => {
    const response = await ApiService.put(`/courses/${id}/main-instructors`, data);
    return response.data;
  },

  getUserCourse: async (userCourseId: number): Promise<UserCourse> => {
    const response = await ApiService.get(`/user-courses/${userCourseId}`);
    return response.data;
  },

  updateCourseReview: async (userCourseId: number, data: CourseReview): Promise<CourseReview> => {
    const response = await ApiService.put(`/user-courses/${userCourseId}/review`, data);
    return response.data as CourseReview;
  },
  discussionsNotifications: async (data: any): Promise<any> => {
    const response = await ApiService.post(`/course/discussions/notifications`, data);
    return response.data;
  },
  getCourseSyllabusAI: async (data: any): Promise<any> => {
    const response = await ApiService.postAI(`/newlane/v1/course-syllabus-generator`, data);
    return response.data;
  },
  getCourseSortDesAI: async (data: any): Promise<any> => {
    const response = await ApiService.postAI(
      `/newlane/v1/course-short-description-generator`,
      data
    );
    return response.data;
  },
  getCourseLongDesAI: async (data: any): Promise<any> => {
    const response = await ApiService.postAI(`/newlane/v1/course-long-description-generator`, data);
    return response.data;
  },
  getCourseLessonSyllabusAI: async (data: any): Promise<any> => {
    const response = await ApiService.postAI(`/newlane/v1/lesson-syllabus-generator`, data);
    return response.data;
  },
  getCourseLessonSortDesAI: async (data: any): Promise<any> => {
    // console.log('course Servise call')
    const response = await ApiService.postAI(
      `/newlane/v1/lesson-short-description-generator`,
      data
    );
    return response.data;
  },
  getCourseLessonLongDesAI: async (data: any): Promise<any> => {
    // console.log('course Servise call')
    const response = await ApiService.postAI(`/newlane/v1/lesson-long-description-generator`, data);
    return response.data;
  },
  getCourseLessonTopicDesAI: async (data: any): Promise<any> => {
    // console.log('course Servise call')
    const response = await ApiService.postAI(`/newlane/v1/topic-description-generator`, data);
    return response.data;
  },
  getCourseLessonTopicAsessmentsAI: async (data: any): Promise<any> => {
    // console.log('course Servise call')
    const response = await ApiService.postAI(`/newlane/v1/assessment-generator`, data);
    return response.data;
  },
  getCourseCompletion: async (courseId: number): Promise<CourseCompletionResponse> => {
    const response = await ApiService.get(`/courses/${courseId}/completion`);
    return response.data;
  },

  startCourse: async (courseId: number): Promise<AxiosResponse<any>> => {
    const response = await ApiService.post(`/courses/${courseId}/start`, {});
    return response;
  },
};

export default CourseService;
